import { useEffect, useState } from 'react';
import { client } from '../../session';

/** One AUTH environment as the onboarding wizards see it (`GET /onboarding/config`): public URLs only. */
export interface OnboardingTarget {
  id: string;
  label: string;
  authUrl: string;
  /** `{authUrl}/api/auth/v1`: the WorkServer's `Auth/ServiceUrl`. */
  authServiceUrl: string;
  scimUrl: string | null;
  buddyUrl: string | null;
  /** `{PrintQueueUrl}/api/printqueue/v1`: a print server's `PrintQueueUri`, the WorkServer's `PrintQueue/ServiceUrl`. */
  printQueueUri: string | null;
  automationGwUrl: string | null;
  /** `wss://…/tunnels/ws`: an agent's `GatewayUrl`. */
  gatewayWsUrl: string | null;
  /** Install bundle of the agent, `{platform}` = `win-x64` | `linux-x64`. */
  agentBundleUrl: string | null;
  agentImage: string | null;
  collectorUrl: string | null;
  boxCalculationUrl: string | null;
}

export interface OnboardingConfig {
  /** The AuthTargets entry that is the hub's own AUTH (registry and statistics link to its tenants). */
  hubTarget: string | null;
  printServer: { windowsUrl: string | null; cupsImage: string | null };
  targets: OnboardingTarget[];
}

let cached: Promise<OnboardingConfig> | null = null;

export const onboardingConfig = (): Promise<OnboardingConfig> => {
  cached ??= client.get<OnboardingConfig>('/onboarding/config').catch((e) => {
    cached = null;
    throw e;
  });
  return cached;
};

/** Test hook. */
export const resetOnboardingConfig = () => void (cached = null);

/** The config once loaded (null meanwhile or when it failed). */
export function useOnboardingConfig(): OnboardingConfig | null {
  const [config, setConfig] = useState<OnboardingConfig | null>(null);
  useEffect(() => {
    let live = true;
    onboardingConfig()
      .then((c) => live && setConfig(c))
      .catch(() => live && setConfig(null));
    return () => {
      live = false;
    };
  }, []);
  return config;
}

export const targetOf = (config: OnboardingConfig | null, id: string): OnboardingTarget | undefined =>
  config?.targets.find((t) => t.id === id);
